import React, { Fragment, useContext, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Helmet } from 'react-helmet-async'
import withEventDetail from '../../containers/with-event-detail'
import withNewsDetail from '../../containers/with-news-detail'
import withOfferDetail from '../../containers/with-offer-detail'
import withStoryDetail from '../../containers/with-story-detail'
import withModules from '../../containers/with-modules'
import Feature from '../../components/feature'
import Modules from '../../components/modules'
import ComponentMetaData from '../../components/component-meta-data'
import { entryType } from '../../types'
import { parseHostname } from '../../helpers/url'
import pagesHoc from '../pages-hoc'
import getEntryStructuredData from '../../helpers/get-entry-structured-data'
import getEntryOpenGraphData from '../../helpers/get-entry-open-graph-data'
import pageLoadDataLayer from '../../helpers/analytics-utils/page-load-data-layer'
import { CONFIG_KEYS, getClientConfigValue } from '../../helpers/config-helper'
import getAccordionEventDetail, {
  getPastOccurrences,
} from '../../helpers/get-accordion-event-detail'
import { ShareButtonType } from '../../components/page-sharer'
import canonicalLink from '../../helpers/canonical-link'
import { HIDE_UTILITY_BOX } from '../../constants'
import {
  GetHrefContext,
  PromotionPositionContext,
  RavenContext,
} from '../../context'
import useCentreTime from '../../helpers/use-centre-time'
import styles from './index.scss'

function getRetailerUtility(entry, centre) {
  const hideUtility = HIDE_UTILITY_BOX.includes(entry.category)
  if (hideUtility || !entry.store) {
    return null
  }
  const sameLocation = entry.physicalAddress === entry.store.physicalAddress
  return {
    storeLink: centre
      ? `/${centre.slug}/store/${entry.store.sys.id}/${entry.store.slug}`
      : '',
    heading: entry.store.storeTitle || 'Visit us',
    phoneNumber: entry.store.phoneNumber,
    emailAddress: entry.store.emailAddress,
    link: entry.store.websiteAddress,
    linkText: parseHostname(entry.store.websiteAddress),
    ...(sameLocation
      ? {
          nearestLocation: entry.store.nearestLocation,
          location: entry.store.physicalAddress,
        }
      : {
          nearestLocation: '',
          location: '',
        }),
    ...(centre && {
      openHours: {
        storeTradingSchedule: entry.store.storeTradingSchedule,
        centreTradingSchedule: centre.centreTradingSchedule,
        timeZone: centre.timeZone,
      },
    }),
    status: entry.store.status,
  }
}

function getStoryCanonicalLink(url, { entry, isEvolved, country }) {
  if (isEvolved && ['CFStory', 'Story'].includes(entry?.typename)) {
    // As per Google's guidelines, setting this as an absolute URL is preferred: https://developers.google.com/search/docs/advanced/crawling/consolidate-duplicate-urls?visit_id=637480936847850987-3273558915&hl=en&rd=2
    return `https://${getClientConfigValue(
      country === 'New Zealand'
        ? CONFIG_KEYS.NEXT_PUBLIC_NZ_HOST
        : CONFIG_KEYS.NEXT_PUBLIC_AU_HOST
    )}/story/${entry.id}/${entry.slug}`
  }

  return canonicalLink.canonical(url)
}

function getComponentMetaData(getHref, raven, props) {
  const { entry, centre } = props

  const url = getStoryCanonicalLink(getHref(), props)

  let structuredData = null
  let openGraphData = null
  try {
    openGraphData = getEntryOpenGraphData({ entry, url })
    structuredData = getEntryStructuredData({ entry, centre, url })
  } catch (err) {
    if (raven) {
      raven.captureException(err, { level: 'warning' })
    }
  }
  return { structuredData, openGraphData }
}

const EntryDetail = props => {
  const { entry, modules, template, centre } = props
  const retailerUtility = getRetailerUtility(entry, centre)
  const getHref = useContext(GetHrefContext)
  const raven = useContext(RavenContext)
  const centreTime = useCentreTime()
  const componentMetaData = getComponentMetaData(getHref, raven, props)
  const validEvent = entry.category === 'Event' && !entry.isExpired
  const emptyOcc = { summary: false, timeZone: '' }
  const shareButtonType = validEvent ? ShareButtonType.EVENT : undefined
  useEffect(() => {
    pageLoadDataLayer(entry, centre)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  // This will prevent propType warnings. As the "occurrences" prop may be removed in future, this work around should be ok....
  return (
    <Fragment>
      <Helmet>
        <title>{`${entry.category} - ${entry.title}`}</title>
        <meta name="description" content={entry.shortDescription} />
      </Helmet>
      <ComponentMetaData data={componentMetaData} />
      <PromotionPositionContext.Provider
        value={template?.analyticsMetadata?.displayId}
      >
        <Feature
          centred
          shareButtonType={shareButtonType}
          lazyLoad={false}
          angleOrigin="top-right"
          position="left"
          topic={entry.topic}
          desktopImageUrl={entry.desktopImage && entry.desktopImage.src}
          mobileImageUrl={entry.mobileImage && entry.mobileImage.src}
          title={entry.title}
          body={entry.body}
          priceInfo={entry.priceInfo}
          terms={entry.termsAndConditions}
          retailerUtility={retailerUtility}
          location={validEvent ? '' : entry.location}
          occurrences={validEvent ? emptyOcc : entry.occurrences}
          callsToAction={entry.callsToAction}
          infoAccordion={
            validEvent && centre && centreTime
              ? getAccordionEventDetail(entry, centre.slug, centreTime)
              : null
          }
          pastEventsAccordion={
            validEvent &&
            centreTime &&
            entry.occurrences &&
            entry.occurrences.pastTimes.length
              ? getPastOccurrences(entry.occurrences.pastTimes, centreTime)
              : null
          }
          analyticsMetadata={entry.analyticsMetadata}
        />
      </PromotionPositionContext.Provider>
      {modules?.length ? (
        <Modules className={styles.initialContentGroup} modules={modules} />
      ) : null}
    </Fragment>
  )
}

EntryDetail.propTypes = {
  entry: entryType.isRequired,
  modules: PropTypes.array,
  centre: PropTypes.object,
  template: PropTypes.shape({
    analyticsMetadata: PropTypes.shape({
      displayId: PropTypes.string,
    }),
  }),
  /* eslint-disable react/no-unused-prop-types */
  // Shhh eslint, these _are_ used
  isEvolved: PropTypes.bool,
  country: PropTypes.string,
  /* eslint-enable */
}

EntryDetail.defaultProps = {
  modules: [],
  isEvolved: false,
}

// TODO: Get this to work if withModules returns error get overall error
export const EventDetail = withModules(withEventDetail(pagesHoc(EntryDetail)))
export const NewsDetail = withModules(withNewsDetail(pagesHoc(EntryDetail)))
export const OfferDetail = withModules(withOfferDetail(pagesHoc(EntryDetail)))
export const StoryDetail = withModules(
  withStoryDetail(pagesHoc(EntryDetail, _ => ({}), getStoryCanonicalLink))
)
